import React, { useState, useEffect } from "react";
import {
  View,
  Text,
  TextInput,
  Button,
  StyleSheet,
  ScrollView,
  Modal,
  Pressable,
  Image, // 👈 добавили
  Animated,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { addToShortTerm } from "./modules/memory.js";
import { CHANGELOG, CHANGELOG_EN } from "./CHANGELOG.js";
import Constants from "expo-constants";
import {
  useFonts,
  PlaypenSans_400Regular,
  PlaypenSans_700Bold,
} from "@expo-google-fonts/playpen-sans";
import usFlag from "./assets/flags/us.svg.png";
import ruFlag from "./assets/flags/ru.svg.png";
import { Audio } from "expo-av";

const PIPELINE_URL = "https://neokid-proxy.onrender.com/neokid-pipeline";
const DRAW_STREAM_URL = "https://neokid-proxy.onrender.com/draw-stream";
const TTS_TYPECAST_URL = "https://neokid-proxy.onrender.com/tts-typecast";
const TTS_ELEVENLABS_URL = "https://neokid-proxy.onrender.com/tts-elevenlabs";
const TTS_OPENAI_URL = "https://neokid-proxy.onrender.com/tts-openai";
const VOICE_CHAT_URL = "https://neokid-proxy.onrender.com/voice-chat";
const REALTIME_SESSION_URL = "https://neokid-proxy.onrender.com/realtime-session";
const VOICE_CHAT_STREAM_URL = "https://neokid-proxy.onrender.com/voice-chat-stream";
const TTS_TYPECAST_STREAM_URL = "https://neokid-proxy.onrender.com/tts-typecast-stream";
const FONT = {
  regular: "PlaypenSans_400Regular",
  bold: "PlaypenSans_700Bold",
};

const LOCALES = {
  ru: {
    title: "NeoKid",
    subtitle: "Поговори с Мишкой",
    inputPlaceholder: "Напиши Мишке…",
    sendButton: "Отправить",
    thinking: "💭 Мишка думает",
    drawing: "🖍️ Мишка рисует",
    changelogTitle: "📱 История изменений",
    closeButton: "Закрыть",
    errorMsg: "Ой! Что-то пошло не так... 😢",
    drawSuccess: "Вот твой рисунок!",
    drawError: "Ой! Не получилось нарисовать... 😢 Попробуй что-нибудь другое.",
    drawPlaceholder: "Что нарисовать?",
    listening: "🎤 Слушаю...",
    processing: "🤔 Думаю...",
    speaking: "🔊 Говорю...",
    idle: "😀 Ожидаю...",
    voiceDrawNotAvailable:
      "Я могу рисовать, но не голосом. Нажми кисточку и попроси меня ещё раз 🎨",
  },
  en: {
    title: "NeoKid",
    subtitle: "Talk to Neo",
    inputPlaceholder: "Write to Neo…",
    sendButton: "Send",
    thinking: "💭 Neo is thinking",
    drawing: "🖍️ Neo is drawing",
    changelogTitle: "📱 Changelog",
    closeButton: "Close",
    errorMsg: "Oops! Something went wrong... 😢",
    drawSuccess: "Here's your drawing!",
    drawError: "Oops! I couldn't draw it... 😢 Ask for something else.",
    drawPlaceholder: "What should I draw?",
    listening: "🎤 Listening...",
    processing: "🤔 Thinking...",
    speaking: "🔊 Speaking...",
    idle: "😀 Waiting...",
    voiceDrawNotAvailable:
      "I can draw, but not by voice yet. Tap the brush and ask me again 🎨",
  },
};

function t(locale, key) {
  // сначала пробуем взять из выбранной локали
  if (LOCALES[locale] && LOCALES[locale][key]) {
    return LOCALES[locale][key];
  }
  // fallback на русский, если ключ не определён в en
  if (LOCALES.ru[key]) {
    return LOCALES.ru[key];
  }
  // крайний случай — сам ключ
  return key;
}

function generateUserId() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

async function sendThroughServerPipeline(userMessage, userId, options = {}) {
  const response = await fetch(PIPELINE_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      message: userMessage,
      userId: userId || "anonymous",
      drawMode: !!options.drawMode,
      drawStyle: options.drawStyle || "crayons",
    }),
  });

  if (!response.ok) {
    console.error("Pipeline error:", response.status, await response.text());
    return {
      content: "Ой, я немножечко сломался… 😢",
      images: [],
      actionType: "text",
      reactionHint: "",
      ratingHint: "",
      actionParams: {},
    };
  }

  const data = await response.json();

  // 💡 На всякий случай добавим безопасные дефолты,
  // чтобы фронт никогда не упал.
  return {
    content: data.content || "",
    images: data.images || [],
    actionType: data.actionType || "text",
    reactionHint: data.reactionHint || "",
    ratingHint: data.ratingHint || "",
    actionParams: data.actionParams || {},
  };
}

function renderFormattedText(text) {
  const parts = [];
  let remaining = text;

  // Ищем *italic* или **italic**
  const regex = /(\*\*([^*]+)\*\*)|(\*([^*]+)\*)/;

  while (true) {
    const match = remaining.match(regex);
    if (!match) {
      parts.push({ type: "text", content: remaining });
      break;
    }

    // Обычный текст ДО форматирования
    if (match.index > 0) {
      parts.push({
        type: "text",
        content: remaining.slice(0, match.index),
      });
    }

    // Внутренний текст (и одиночные и двойные звёздочки)
    const italicContent = match[2] || match[4];

    parts.push({
      type: "italic",
      content: italicContent,
    });

    // Укорачиваем строку
    remaining = remaining.slice(match.index + match[0].length);
  }

  return parts;
}

// function AnimatedBubble({ role, style, children }) {
//   const translateY = React.useRef(new Animated.Value(-10)).current;
//   const opacity = React.useRef(new Animated.Value(0)).current;

//   React.useEffect(() => {
//     Animated.parallel([
//       Animated.timing(translateY, {
//         toValue: 0,
//         duration: 220,
//         useNativeDriver: true,
//       }),
//       Animated.timing(opacity, {
//         toValue: 1,
//         duration: 220,
//         useNativeDriver: true,
//       }),
//     ]).start();
//   }, []);

//   return (
//     <Animated.View
//       style={[
//         styles.messageBubbleBase,
//         role === 'user'
//           ? styles.messageBubbleUser
//           : styles.messageBubbleAssistant,
//         style,
//         {
//           transform: [{ translateY }],
//           opacity,
//         },
//       ]}
//     >
//       {children}
//     </Animated.View>
//   );
// }

export default function App() {
  const [fontsLoaded] = useFonts({
    PlaypenSans_400Regular,
    PlaypenSans_700Bold,
  });
  const version = Constants.expoConfig.version;
  const [showChangelog, setShowChangelog] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentAssistantReply, setCurrentAssistantReply] = useState("");
  const [assistantImages, setAssistantImages] = useState([]); // 👈 новый стейт
  const [isDrawing, setIsDrawing] = useState(false);
  const [userId, setUserId] = useState(null);
  const [imageModalVisible, setImageModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [thinkingDots, setThinkingDots] = useState("");
  const [locale, setLocale] = useState("ru"); // 👈 НОВЫЙ СТЕЙТ ЛОКАЛИ
  const changelogText = locale === "en" ? CHANGELOG_EN : CHANGELOG;
  const [drawMode, setDrawMode] = useState(false);
  const [drawStyle, setDrawStyle] = useState("crayons");
  const [speakingKey, setSpeakingKey] = useState(null);
  const currentAudioRef = React.useRef(null);
  const [voiceMode, setVoiceMode] = useState(false);
  const [voicePhase, setVoicePhase] = useState("idle");
  const [recording, setRecording] = useState(null);
  const barCount = 12;
  const peerConnectionRef = React.useRef(null);
  const dataChannelRef = React.useRef(null);
  const remoteAudioRef = React.useRef(null);
  const localStreamRef = React.useRef(null);
  const [realtimeConnected, setRealtimeConnected] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState("offline");
  const responseInFlightRef = React.useRef(false);
  const activeResponseIdRef = React.useRef(null);
  const audioContextRef = React.useRef(null);
  const analyserRef = React.useRef(null);
  const micSourceRef = React.useRef(null);
  const vadIntervalRef = React.useRef(null);
  const micStreamRef = React.useRef(null);
  const silenceStartRef = React.useRef(null);
  const lastVadLogRef = React.useRef(0);
  const isStoppingRef = React.useRef(false);
  const recordingRef = React.useRef(null);
  const speechStartedRef = React.useRef(false);
  const recordingStartedAtRef = React.useRef(null);
  const speechStartedAtRef = React.useRef(null);
  const voiceModeRef = React.useRef(false);
  const currentAssistantReplyRef = React.useRef("");
  const assistantImagesRef = React.useRef([]);
  
  const VAD_THRESHOLD = 0.045; // чувствительность (подкрутим)
  const VAD_SILENCE_MS = 1600; // сколько ждать тишину
  const MIN_RECORDING_MS = 700;
  const MAX_SILENCE_BEFORE_SPEECH_MS = 5000;
  const MAX_RECORDING_MS = 10000; // 10 сек
  const MIN_SPEECH_DURATION_MS = 700;

  const bars = React.useRef(
    Array.from({ length: barCount }, () => new Animated.Value(0.2)),
  ).current;

  useEffect(() => {
    (async () => {
      try {
        const storedId = await AsyncStorage.getItem("neokid_user_id");
        if (storedId) {
          setUserId(storedId);
        } else {
          const newId = generateUserId();
          await AsyncStorage.setItem("neokid_user_id", newId);
          setUserId(newId);
        }
      } catch (err) {
        console.error("Failed to init userId", err);
        // на крайняк пусть будет аноним
        if (!userId) {
          const fallback = generateUserId();
          setUserId(fallback);
        }
      }
    })();
  }, []);

  useEffect(() => {
    currentAssistantReplyRef.current = currentAssistantReply;
  }, [currentAssistantReply]);

  useEffect(() => {
    assistantImagesRef.current = assistantImages;
  }, [assistantImages]);

  // useEffect(() => {
  //   LayoutAnimation.configureNext(
  //     LayoutAnimation.create(
  //       250, // длительность, можно 200–300
  //       LayoutAnimation.Types.easeInEaseOut,
  //       LayoutAnimation.Properties.opacity
  //     )
  //   );
  // }, [
  //   conversationHistory.length,
  //   loading,
  //   currentAssistantReply,
  //   assistantImages?.length,
  // ]);

  function animateBars(phase) {
    bars.forEach((bar, i) => {
      bar.stopAnimation();

      if (phase === "listening") {
        // активная "живая" волна
        Animated.loop(
          Animated.sequence([
            Animated.timing(bar, {
              toValue: Math.random() * 1,
              duration: 200 + i * 20,
              useNativeDriver: false,
            }),
            Animated.timing(bar, {
              toValue: 0.2,
              duration: 200 + i * 20,
              useNativeDriver: false,
            }),
          ]),
        ).start();
      }

      if (phase === "processing") {
        // спокойное дыхание
        Animated.loop(
          Animated.sequence([
            Animated.timing(bar, {
              toValue: 0.4,
              duration: 400,
              useNativeDriver: false,
            }),
            Animated.timing(bar, {
              toValue: 0.2,
              duration: 400,
              useNativeDriver: false,
            }),
          ]),
        ).start();
      }

      if (phase === "speaking") {
        // более “плавная речь”
        Animated.loop(
          Animated.sequence([
            Animated.timing(bar, {
              toValue: 0.7,
              duration: 180 + i * 10,
              useNativeDriver: false,
            }),
            Animated.timing(bar, {
              toValue: 0.3,
              duration: 180 + i * 10,
              useNativeDriver: false,
            }),
          ]),
        ).start();
      }

      if (phase === "idle") {
        bar.setValue(0.2);
      }
    });
  }

  useEffect(() => {
    voiceModeRef.current = voiceMode;
  }, [voiceMode]);

  useEffect(() => {
    animateBars(voicePhase);
  }, [voicePhase]);

  useEffect(() => {
    if (!loading) {
      setThinkingDots("");
      return;
    }

    let i = 0;
    const interval = setInterval(() => {
      i = (i + 1) % 4; // 0,1,2,3
      setThinkingDots(".".repeat(i)); // "", ".", "..", "..."
    }, 400); // скорость мигания — можно потом подстроить

    return () => clearInterval(interval);
  }, [loading]);

  const changeLocale = async (newLocale) => {
    if (newLocale !== "ru" && newLocale !== "en") return;
    setLocale(newLocale);
    try {
      await AsyncStorage.setItem("neokid_locale", newLocale);
    } catch (err) {
      console.warn("Failed to save locale", err);
    }
  };

  if (!fontsLoaded) return null;

  async function startStreamingDraw(userMessage, styleFromPlanner) {
    // fallback, если вдруг style не пришёл
    const finalStyle = styleFromPlanner || "crayons";
    // Fallback: если в окружении нет EventSource (например, не web)
    if (typeof EventSource === "undefined") {
      console.warn(
        "EventSource is not supported, falling back to normal pipeline",
      );
      setLoading(true);
      try {
        const finalReply = await sendThroughServerPipeline(userMessage);
        setCurrentAssistantReply(finalReply.content || "");
        if (finalReply.images && finalReply.images.length > 0) {
          setAssistantImages(finalReply.images);
        }
        addToShortTerm("assistant", finalReply.content || "");
      } catch (err) {
        console.error("❌ Ошибка в fallback-пайплайне:", err);
        const fallback = t(locale, "errorMsg");
        setCurrentAssistantReply(fallback);
        setAssistantImages([]);
        addToShortTerm("assistant", fallback);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setAssistantImages([]);
    setCurrentAssistantReply(""); // текст появится в конце

    const url =
      DRAW_STREAM_URL +
      `?prompt=${encodeURIComponent(userMessage)}` +
      `&style=${encodeURIComponent(finalStyle)}` +
      `&userId=${userId}` +
      "&size=1024x1024&quality=low&partial_images=3";

    console.log("🎨 [startStreamingDraw] URL:", url);
    const es = new EventSource(url);

    es.addEventListener("hello", (e) => {
      console.log("hello from /draw-stream:", e.data);
    });

    es.addEventListener("partial", (e) => {
      try {
        const data = JSON.parse(e.data);
        // обновляем картинку в реальном времени
        setAssistantImages([{ url: data.url }]);
      } catch (err) {
        console.error("partial parse error:", err);
      }
    });

    es.addEventListener("final", (e) => {
      try {
        const data = JSON.parse(e.data);
        const msg = t(locale, "drawSuccess");
        // финальная картинка
        setAssistantImages([{ url: data.url }]);
        setCurrentAssistantReply(msg);
        addToShortTerm("assistant", msg);
      } catch (err) {
        console.error("final parse error:", err);
        const fallback = t(locale, "drawSuccess");
        setCurrentAssistantReply(fallback);
        addToShortTerm("assistant", fallback);
      } finally {
        setLoading(false);
        setIsDrawing(false); // ← добавляем эту строку!
        es.close();
      }
    });

    es.addEventListener("error", (e) => {
      console.error("stream error", e);
      setLoading(false);
      const fallback = t(locale, "drawError");
      setCurrentAssistantReply(fallback);
      setAssistantImages([]);
      addToShortTerm("assistant", fallback);
      es.close();
    });
  }

  async function cleanupVAD() {
    if (vadIntervalRef.current) {
      clearInterval(vadIntervalRef.current);
      vadIntervalRef.current = null;
    }

    silenceStartRef.current = null;

    if (audioContextRef.current) {
      await audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
  }

  async function playStreamingTTS(text) {
    if (!text || !text.trim()) return;

    const response = await fetch(TTS_TYPECAST_STREAM_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });

    if (!response.ok || !response.body) {
      console.error("TTS stream failed:", response.status);
      return;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContextClass({ sampleRate: 32000 });

    const reader = response.body.getReader();

    let firstChunk = true;
    let playTime = audioContext.currentTime + 0.08;
    let sampleRate = 32000;
    let channels = 1;
    let bitsPerSample = 16;
    let headerParsed = false;
    let pcmRemainder = new Uint8Array(0);

    function playPcmChunk(uint8Array) {
      // PCM16 mono little-endian
      const sampleCount = Math.floor(uint8Array.byteLength / 2);
      if (sampleCount <= 0) return;

      const audioBuffer = audioContext.createBuffer(1, sampleCount, sampleRate);

      const channelData = audioBuffer.getChannelData(0);
      const view = new DataView(
        uint8Array.buffer,
        uint8Array.byteOffset,
        uint8Array.byteLength,
      );

      for (let i = 0; i < sampleCount; i++) {
        const int16 = view.getInt16(i * 2, true);
        channelData[i] = int16 / 32768;
      }

      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);

      const startAt = Math.max(playTime, audioContext.currentTime);
      source.start(startAt);

      playTime = startAt + audioBuffer.duration;
    }

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      if (!value || value.byteLength === 0) continue;

      let chunk = value;

      // Первый chunk содержит WAV header 44 bytes
      if (firstChunk) {
        firstChunk = false;

        const header = chunk.slice(0, 44);
        const view = new DataView(
          chunk.buffer,
          chunk.byteOffset,
          chunk.byteLength,
        );

        channels = view.getUint16(22, true);
        sampleRate = view.getUint32(24, true);
        bitsPerSample = view.getUint16(34, true);

        console.log("🎧 WAV header:", { channels, sampleRate, bitsPerSample });

        chunk = chunk.slice(44);
      }

      if (pcmRemainder.length > 0) {
        const merged = new Uint8Array(pcmRemainder.length + chunk.length);
        merged.set(pcmRemainder, 0);
        merged.set(chunk, pcmRemainder.length);
        chunk = merged;
        pcmRemainder = new Uint8Array(0);
      }

      if (chunk.length % 2 !== 0) {
        pcmRemainder = chunk.slice(chunk.length - 1);
        chunk = chunk.slice(0, chunk.length - 1);
      }

      if (chunk.length > 0) {
        playPcmChunk(chunk);
      }

      // playPcmChunk(chunk);
    }

    // ждём, пока последний запланированный кусок доиграет
    const waitMs = Math.max(0, (playTime - audioContext.currentTime) * 1000);

    await new Promise((resolve) => setTimeout(resolve, waitMs + 100));

    await audioContext.close().catch(() => {});
  }

  async function startRealtimeVoice() {
    try {
      setRealtimeStatus("connecting");

      const sessionResponse = await fetch(REALTIME_SESSION_URL, {
        method: "POST",
      });

      if (!sessionResponse.ok) {
        console.error(
          "Realtime session error:",
          sessionResponse.status,
          await sessionResponse.text(),
        );
        setRealtimeStatus("error");
        return;
      }

      const { client_secret } = await sessionResponse.json();

      if (!client_secret) {
        console.error("No realtime client_secret returned");
        setRealtimeStatus("error");
        return;
      }

      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      const remoteAudio = new window.Audio();
      remoteAudio.autoplay = true;
      remoteAudioRef.current = remoteAudio;

      pc.ontrack = (event) => {
        remoteAudio.srcObject = event.streams[0];
      };

      const localStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });

      localStreamRef.current = localStream;

      localStream.getTracks().forEach((track) => {
        pc.addTrack(track, localStream);
      });

      const dc = pc.createDataChannel("oai-events");
      dataChannelRef.current = dc;

      dc.onopen = () => {
        console.log("✅ Realtime data channel open");
        setRealtimeStatus("connected");
        setRealtimeConnected(true);

        // dc.send(
        //   JSON.stringify({
        //     type: "response.create",
        //     response: {
        //       modalities: ["audio", "text"],
        //       instructions: "Say a short hello in Russian.",
        //     },
        //   }),
        // );
      };

      dc.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // 🟢 новый ответ
          if (data.type === "response.created") {
            activeResponseIdRef.current = data.response?.id;
            responseInFlightRef.current = true;

            console.log("🟢 new response:", activeResponseIdRef.current);
            return;
          }

          // ⛔ фильтр старых ответов
          if (
            data.response_id &&
            data.response_id !== activeResponseIdRef.current
          ) {
            return;
          }

          // 🔊 аудио остановилось
          if (data.type === "output_audio_buffer.stopped") {
            console.log("audio stopped:", data.response_id);
          }

          // ✅ завершено
          if (data.type === "response.completed") {
            console.log("✅ completed");

            responseInFlightRef.current = false;
            activeResponseIdRef.current = null;
          }
        } catch (err) {
          console.log("📡 raw:", event.data);
        }
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResponse = await fetch(
        "https://api.openai.com/v1/realtime?model=gpt-realtime",
        {
          method: "POST",
          body: offer.sdp,
          headers: {
            Authorization: `Bearer ${client_secret}`,
            "Content-Type": "application/sdp",
          },
        },
      );

      if (!sdpResponse.ok) {
        console.error(
          "Realtime SDP error:",
          sdpResponse.status,
          await sdpResponse.text(),
        );
        setRealtimeStatus("error");
        return;
      }

      const answerSdp = await sdpResponse.text();

      await pc.setRemoteDescription({
        type: "answer",
        sdp: answerSdp,
      });
    } catch (err) {
      console.error("❌ startRealtimeVoice error:", err);
      setRealtimeStatus("error");
    }
  }

  function sendRealtimeMessage(text) {
    if (!dataChannelRef.current) return;

    if (responseInFlightRef.current) {
      console.log("⛔ already generating, skip");
      return;
    }

    responseInFlightRef.current = true;

    dataChannelRef.current.send(
      JSON.stringify({
        type: "response.create",
        response: {
          modalities: ["audio", "text"],
          instructions: text,
        },
      }),
    );
  }

  function stopRealtimeVoice() {
    try {
      console.log("🛑 stopping realtime");

      // data channel
      if (dataChannelRef.current) {
        dataChannelRef.current.close();
        dataChannelRef.current = null;
      }

      // peer connection
      if (peerConnectionRef.current) {
        peerConnectionRef.current.getSenders().forEach((s) => s.track?.stop());
        peerConnectionRef.current.close();
        peerConnectionRef.current = null;
      }

      // микрофон
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach((t) => t.stop());
        localStreamRef.current = null;
      }

      // аудио
      if (remoteAudioRef.current) {
        remoteAudioRef.current.pause();
        remoteAudioRef.current.srcObject = null;
        remoteAudioRef.current = null;
      }

      setRealtimeConnected(false);
      setRealtimeStatus("offline");
    } catch (err) {
      console.error("❌ stopRealtimeVoice error:", err);
    }
  }

  function startVoiceMode() {
    if (voiceMode) return;

    setVoiceMode(true);
    setVoicePhase("listening");

    startRecordingOnly(); // 🔥 ВСЁ, больше ничего
  }

  async function stopVoiceMode() {
    try {
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.src = "";
        currentAudioRef.current = null;
      }

      if (recordingRef.current) {
        await recordingRef.current.stopAndUnloadAsync();
        recordingRef.current = null;
        setRecording(null);
      }

      await cleanupVAD();

      setVoiceMode(false);
      setVoicePhase("idle");
      setLoading(false);
    } catch (err) {
      console.error("❌ stopVoiceMode error:", err);
    }
  }

  async function startRecordingOnly() {
    try {
      console.log("🟢 startRecordingOnly triggered");

      setVoicePhase("listening");

      if (recording) {
        console.log("⚠️ recording already exists");
        return;
      }

      const permission = await Audio.requestPermissionsAsync();

      if (!permission.granted) {
        alert("Нужен доступ к микрофону");
        return;
      }

      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
      });

      // 🎤 создаём stream для VAD
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const micSource = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 512;
      micSource.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      micSourceRef.current = micSource;

      // 🎤 Expo запись
      const result = await Audio.Recording.createAsync(
        Audio.RecordingOptionsPresets.HIGH_QUALITY,
      );

      recordingRef.current = result.recording;
      setRecording(result.recording);

      speechStartedRef.current = false;
      speechStartedAtRef.current = null;
      silenceStartRef.current = null;
      recordingStartedAtRef.current = Date.now();

      startVAD();
    } catch (err) {
      console.error("🎤 startRecordingOnly error:", err);
      setVoicePhase("idle");
    }
  }

  async function stopRecordingOnly() {
    try {
      if (isStoppingRef.current) return;
      isStoppingRef.current = true;

      console.log("🛑 stopRecordingOnly triggered");

      const activeRecording = recordingRef.current;

      if (!activeRecording) {
        console.log("⚠️ no active recording in ref");
        return;
      }

      setVoicePhase("processing");

      // ✅ сначала останавливаем реальную запись из ref
      await activeRecording.stopAndUnloadAsync();

      const uri = activeRecording.getURI();

      recordingRef.current = null;
      setRecording(null);

      // ✅ потом чистим VAD
      await cleanupVAD();

      if (!speechStartedRef.current) {
        console.log("⏹️ no speech recorded, cancel");
        setVoicePhase("idle");
        return;
      }

      const speechDuration = speechStartedAtRef.current
        ? Date.now() - speechStartedAtRef.current
        : 0;

      if (speechDuration < MIN_SPEECH_DURATION_MS) {
        console.log("⚠️ speech too short, skip STT", { speechDuration });
        setVoicePhase("idle");
        return;
      }

      if (!uri) {
        console.log("⚠️ empty audio");
        setVoicePhase("idle");
        return;
      }

      await sendRecordedVoiceToChain(uri);
    } catch (err) {
      console.error("❌ stopRecordingOnly error:", err);
      setVoicePhase("idle");
    } finally {
      isStoppingRef.current = false;
      setLoading(false);
    }
  }

  function startVAD() {
    const analyser = analyserRef.current;
    if (!analyser) return;

    const dataArray = new Uint8Array(analyser.fftSize);

    vadIntervalRef.current = setInterval(() => {
      analyser.getByteTimeDomainData(dataArray);

      // RMS / громкость
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const v = (dataArray[i] - 128) / 128;
        sum += v * v;
      }

      const rms = Math.sqrt(sum / dataArray.length);
      const now = Date.now();

      // debug log
      if (now - lastVadLogRef.current > 500) {
        console.log("🎙️ rms:", rms.toFixed(4), {
          threshold: VAD_THRESHOLD,
          speechStarted: speechStartedRef.current,
          silenceStart: silenceStartRef.current,
        });
        lastVadLogRef.current = now;
      }

      // 0) Если речь ещё не начиналась — НЕ останавливаем запись
      // если пользователь вообще не начал говорить
      if (!speechStartedRef.current) {
        if (recordingStartedAtRef.current && now - recordingStartedAtRef.current > MAX_SILENCE_BEFORE_SPEECH_MS) {
          console.log("⏹️ auto cancel: no speech");
          stopRecordingOnly();
          return;
        }
      }
      
      // 1) Если громкость выше порога — считаем, что речь началась/продолжается
      if (rms >= VAD_THRESHOLD) {
        if (!speechStartedRef.current) {
          console.log("🟢 speech started");
          speechStartedRef.current = true;
          speechStartedAtRef.current = now; // 👈 ВОТ ЭТОГО НЕ ХВАТАЕТ
        }

        silenceStartRef.current = null;
        return;
      }

      // 1.1) Если речи ещё не было — ничего не делаем
      if (!speechStartedRef.current) {
        return;
      }

      // 2) Защита от слишком короткой записи
      if (
        recordingStartedAtRef.current &&
        now - recordingStartedAtRef.current < MIN_RECORDING_MS
      ) {
        return;
      }

      if (now - recordingStartedAtRef.current > MAX_RECORDING_MS) {
        console.log("⏹️ max recording reached");

        stopRecordingOnly();
        return;
      }

      // 3 Речь была, теперь ловим тишину после речи
      if (!silenceStartRef.current) {
        silenceStartRef.current = now;
      }

      const silenceDuration = now - silenceStartRef.current;

      if (silenceDuration > VAD_SILENCE_MS) {
        console.log("🛑 silence after speech detected → stop");
        stopRecordingOnly();
      }
    }, 100);
  }

  function getVoiceStatusText() {
    switch (voicePhase) {
      case "listening":
        return t(locale, "listening");
      case "processing":
        return t(locale, "processing");
      case "speaking":
        return t(locale, "speaking");
      default:
        return t(locale, "idle");
    }
  }

  async function sendRecordedVoiceToChain(uri) {
    try {
      setVoicePhase("processing");
      setLoading(true);

      const audioResponse = await fetch(uri);
      const rawBlob = await audioResponse.blob();

      console.log("recorded blob type:", rawBlob.type);
      console.log("recorded blob size:", rawBlob.size);
      if (rawBlob.size < 5000) {
        console.log("⚠️ audio too short, skip");
        setVoicePhase("idle");
        return;
      }

      const mimeType = rawBlob.type || "audio/webm";

      let fileName = "voice.webm";

      if (mimeType.includes("mp4") || mimeType.includes("m4a")) {
        fileName = "voice.m4a";
      } else if (mimeType.includes("mpeg") || mimeType.includes("mp3")) {
        fileName = "voice.mp3";
      } else if (mimeType.includes("wav")) {
        fileName = "voice.wav";
      }

      const audioFile = new File([rawBlob], fileName, {
        type: mimeType,
      });

      const formData = new FormData();
      formData.append("audio", audioFile);
      formData.append("userId", userId || "test-user");

      const t0 = Date.now();

      const response = await fetch(VOICE_CHAT_STREAM_URL, {
        method: "POST",
        body: formData,
      });

      if (!response.ok || !response.body) {
        const errorText = await response.text().catch(() => "");
        console.error("Voice stream error:", response.status, errorText);

        const fallback =
          locale === "en"
            ? "I didn’t hear you well. Can you say it again? 🎤"
            : "Я тебя не расслышал. Повтори ещё раз? 🎤";

        setCurrentAssistantReply(fallback);
        currentAssistantReplyRef.current = fallback;
        setVoicePhase("idle");
        return;
      }

      const previousAssistantReply = currentAssistantReplyRef.current;
      const previousAssistantImages = assistantImagesRef.current || [];

      if (previousAssistantReply || previousAssistantImages.length) {
        setConversationHistory((prev) => [
          {
            role: "assistant",
            content: previousAssistantReply,
            images: [...previousAssistantImages],
          },
          ...prev,
        ]);
      }

      currentAssistantReplyRef.current = "";
      assistantImagesRef.current = [];
      setCurrentAssistantReply("");
      setAssistantImages([]);

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";
      let currentEvent = "";

      while (true) {
        const { done, value } = await reader.read();

        if (value) {
          buffer += decoder.decode(value, { stream: true });
        }

        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const rawEvent of events) {
          const lines = rawEvent.split("\n");

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              currentEvent = line.replace("event: ", "").trim();
            }

            if (line.startsWith("data: ")) {
              const dataText = line.replace("data: ", "").trim();
              if (!dataText) continue;

              const data = JSON.parse(dataText);

              if (currentEvent === "transcript") {
                setConversationHistory((prev) => [
                  { role: "user", content: data.text },
                  ...prev,
                ]);
              }

              if (currentEvent === "delta") {
                currentAssistantReplyRef.current += data.chunk || "";
                setCurrentAssistantReply(currentAssistantReplyRef.current);
              }

              if (currentEvent === "error") {
                const fallback =
                  data.message ||
                  (locale === "en"
                    ? "I didn’t hear you well. Can you say it again? 🎤"
                    : "Я тебя не расслышал. Повтори ещё раз? 🎤");

                currentAssistantReplyRef.current = fallback;
                setCurrentAssistantReply(fallback);
                setVoicePhase("idle");
                return;
              }

              if (currentEvent === "done") {
                setLoading(false);

                const text = currentAssistantReplyRef.current;

                if (text?.trim()) {
                  setVoicePhase("speaking");

                  try {
                    await playStreamingTTS(text);
                  } catch (err) {
                    console.error("❌ playVoiceTTS error:", err);
                  }
                }

                setVoicePhase("idle");

                setTimeout(() => {
                  if (
                    voiceModeRef.current &&
                    !recordingRef.current &&
                    !isStoppingRef.current
                  ) {
                    startRecordingOnly();
                  }
                }, 300);
              }
            }
          }
        }

        if (done) break;
      }

      // setVoicePhase("idle");
    } catch (err) {
      console.error("❌ sendRecordedVoiceToChain error:", err);
      setCurrentAssistantReply("Ой! Не получилось отправить голос… 😢");
      setVoicePhase("idle");
    } finally {
      // Если audio.play() вернул управление через onended, loading снимется там
      if (!currentAudioRef.current) {
        setLoading(false);
      }
    }
  }

  async function playVoiceTTS(text) {
    if (!text || !text.trim()) return;

    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current.src = "";
      currentAudioRef.current = null;
    }

    const response = await fetch(TTS_TYPECAST_STREAM_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ text }),
    });

    if (!response.ok) {
      console.error("Voice TTS error:", response.status, await response.text());
      return;
    }

    const blob = await response.blob();
    const audioUrl = URL.createObjectURL(blob);
    const audio = new window.Audio(audioUrl);

    currentAudioRef.current = audio;

    return new Promise((resolve) => {
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        resolve();
      };

      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        resolve();
      };

      audio.play().catch((err) => {
        console.error("Voice TTS play error:", err);
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        resolve();
      });
    });
  }

  async function speakAssistantText(text, key) {
    try {
      if (!text || !text.trim()) return;

      // stop previous audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current.src = "";
        currentAudioRef.current = null;
      }

      setSpeakingKey(key);

      const response = await fetch(TTS_TYPECAST_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text,
        }),
      });

      if (!response.ok) {
        console.error("TTS error:", response.status, await response.text());
        setSpeakingKey(null);
        return;
      }

      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      const audio = new window.Audio(audioUrl);

      currentAudioRef.current = audio;

      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        setSpeakingKey(null);
      };

      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        setSpeakingKey(null);
      };

      await audio.play();
    } catch (err) {
      console.error("❌ speakAssistantText error:", err);
      setSpeakingKey(null);
    }
  }

  const sendMessage = async () => {
    let drawingPlanned = false;
    if (!input.trim()) return;

    // если был прошлый ответ ассистента — переносим его в историю
    if (currentAssistantReply || (assistantImages && assistantImages.length)) {
      // animateBubbles();
      setConversationHistory((prev) => {
        const updated = [
          {
            role: "assistant",
            content: currentAssistantReply,
            images: assistantImages ? [...assistantImages] : [], // 👈 кладём картинки в историю
          },
          ...prev,
        ];
        return updated;
      });
    }

    const userMessage = input.trim();

    setCurrentAssistantReply("");
    setAssistantImages([]);

    // добавляем сообщение пользователя в краткосрочную память и историю
    addToShortTerm("user", userMessage);
    // animateBubbles();
    setConversationHistory((prev) => {
      const updated = [{ role: "user", content: userMessage }, ...prev];
      return updated;
    });

    // очищаем инпут
    setInput("");
    setLoading(true);
    setIsDrawing(false);
    const wasDrawMode = drawMode;

    if (wasDrawMode) {
      setDrawMode(false);
    }

    try {
      // 🔥 запускаем ПОЛНЫЙ пайплайн:
      // semantic → intent → ageFilter → responsePlanner → generateOutput
      const finalReply = await sendThroughServerPipeline(
        userMessage,
        userId || "anonymous",
        {
          drawMode,
          drawStyle,
        },
      );

      const { content, images, actionType, actionParams } = finalReply;

      const isDrawAction = actionType === "draw_and_text";

      if (isDrawAction) {
        drawingPlanned = true;

        setCurrentAssistantReply("");
        setAssistantImages([]);
        setIsDrawing(true);
        setLoading(true);

        const drawPrompt = actionParams?.drawPrompt || userMessage;
        const selectedDrawStyle = actionParams?.style || drawStyle || "crayons";

        console.log(
          "🎨 [sendMessage] drawPrompt/style:",
          drawPrompt,
          selectedDrawStyle,
        );

        await startStreamingDraw(drawPrompt, selectedDrawStyle);
      } else {
        setCurrentAssistantReply(content || "");
        setAssistantImages(images || []);
        setIsDrawing(false);
      }

      // пишем ответ в краткосрочную память
      addToShortTerm("assistant", finalReply.content || "");
    } catch (err) {
      console.error("❌ Ошибка в sendMessage:", err);
      const fallback = t(locale, errorMsg);

      setCurrentAssistantReply(fallback);
      setAssistantImages([]); // 👈 очищаем картинки при ошибке
      addToShortTerm("assistant", fallback);
    } finally {
      // 🔑 Гасим loading ТОЛЬКО если не планировали стриминг
      if (!drawingPlanned) {
        setLoading(false);
        setIsDrawing(false); // ← добавляем эту строку!
      }
      // для стрима setLoading(false) уже делает startStreamingDraw в final/error
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.pageWrapper}>
        <View style={styles.contentWrapper}>
          <View style={styles.localeSwitcher}>
            <Pressable
              onPress={() => changeLocale(locale === "ru" ? "en" : "ru")}
              style={styles.localeButton}
            >
              <Image
                source={locale === "ru" ? usFlag : ruFlag}
                style={styles.localeFlag}
              />
            </Pressable>
          </View>
          <View style={styles.appContent}>
            <Text style={styles.title}>{t(locale, "subtitle")}</Text>

            <View style={styles.inputRow}>
              <TextInput
                style={styles.input}
                placeholder={
                  drawMode
                    ? t(locale, "drawPlaceholder")
                    : t(locale, "inputPlaceholder")
                }
                placeholderTextColor="#5b5a5aff"
                value={input}
                onChangeText={setInput}
                editable={!loading && !voiceMode}
                onSubmitEditing={sendMessage}
                returnKeyType="send"
                multiline={true}
                textAlignVertical="top" // важно для Android
                scrollEnabled={true}
                onKeyPress={(e) => {
                  if (
                    e.nativeEvent.key === "Enter" &&
                    !e.nativeEvent.shiftKey
                  ) {
                    e.preventDefault?.();
                    sendMessage();
                  }
                }}
              />

              <View style={styles.drawControlWrapper}>
                {drawMode && !voiceMode && (
                  <View style={styles.drawStyleMenuDown}>
                    {[
                      { key: "crayons", icon: "🖍️" },
                      { key: "pencil", icon: "✏️" },
                      { key: "watercolor", icon: "🖌️" },
                      { key: "marker", icon: "🖋️" },
                    ].map((item) => (
                      <Pressable
                        key={item.key}
                        onPress={() => setDrawStyle(item.key)}
                        style={[
                          styles.drawStyleButton,
                          drawStyle === item.key &&
                            styles.drawStyleButtonActive,
                        ]}
                      >
                        <Text style={styles.drawStyleIcon}>{item.icon}</Text>
                      </Pressable>
                    ))}
                  </View>
                )}

                <Pressable
                  disabled={loading || voiceMode}
                  onPress={() => {
                    if (loading || voiceMode) return;

                    setDrawMode((prev) => {
                      const next = !prev;
                      if (next) setDrawStyle("crayons");
                      return next;
                    });
                  }}
                  style={[
                    styles.drawButton,
                    drawMode && styles.drawButtonActive,
                    (loading || voiceMode) && styles.disabledControl,
                  ]}
                >
                  <Text style={styles.drawButtonText}>🎨</Text>
                </Pressable>
              </View>

              <Pressable
                onPress={startVoiceMode}
                style={[
                  styles.voiceButton,
                  (loading || voiceMode) && styles.disabledControl,
                ]}
                disabled={loading || voiceMode}
              >
                <Text style={styles.voiceButtonText}>🎤</Text>
              </Pressable>

              {/* <Pressable
                onPress={
                  realtimeConnected ? stopRealtimeVoice : startRealtimeVoice
                }
                style={styles.voiceButton}
              >
                <Text style={styles.voiceButtonText}>
                  {realtimeConnected ? "⏹️" : "⚡"}
                </Text>
              </Pressable> */}

              {voiceMode && (
                <View style={styles.voiceOverlay}>
                  <View style={styles.waveformRow}>
                    <View style={styles.waveformContainer}>
                      {bars.map((bar, i) => (
                        <Animated.View
                          key={i}
                          style={[
                            styles.waveBar,
                            {
                              height: bar.interpolate({
                                inputRange: [0, 1],
                                outputRange: [6, 22],
                              }),
                            },
                          ]}
                        />
                      ))}
                    </View>

                    <Text style={styles.voiceStatusText}>
                      {getVoiceStatusText()}
                    </Text>
                  </View>
                  <View style={styles.voiceOverlayButtons}>
                    <Pressable
                      onPress={stopVoiceMode}
                      style={styles.voiceStopButton}
                    >
                      <Text style={styles.voiceStopButtonText}>✕</Text>
                    </Pressable>
                  </View>
                </View>
              )}
            </View>

            <Button
              title={t(locale, "sendButton")}
              onPress={sendMessage}
              disabled={loading}
            />

            <View style={styles.replyContainer}>
              <ScrollView style={styles.replyBox}>
                {/* 1) Статус: Мишка думает / рисует */}
                {loading && !voiceMode && (
                  <Text style={styles.systemLine}>
                    {isDrawing ? t(locale, "drawing") : t(locale, "thinking")}
                    {thinkingDots}
                  </Text>
                )}

                {/* 2) Текущий ответ ассистента (текст ± картинка) */}
                {(currentAssistantReply ||
                  (assistantImages && assistantImages.length > 0)) && (
                  <View style={styles.messageRow}>
                    <View
                      style={[
                        styles.messageBubbleBase,
                        styles.messageBubbleAssistant,
                        { marginBottom: 0 },
                      ]}
                    >
                      {/* Текст – если он есть и мы не в loading */}
                      {currentAssistantReply ? (
                        <View>
                          <Text style={styles.messageBubbleTextBold}>
                            👶{" "}
                            {renderFormattedText(currentAssistantReply).map(
                              (part, i) => {
                                if (part.type === "italic") {
                                  return (
                                    <Text
                                      key={i}
                                      style={{ fontStyle: "italic" }}
                                    >
                                      {part.content}
                                    </Text>
                                  );
                                }
                                return <Text key={i}>{part.content}</Text>;
                              },
                            )}
                          </Text>

                          <Pressable
                            onPress={() =>
                              speakAssistantText(
                                currentAssistantReply,
                                "current",
                              )
                            }
                            style={styles.speakerButton}
                          >
                            <Text style={styles.speakerButtonText}>
                              {speakingKey === "current" ? "🔊" : "🔈"}
                            </Text>
                          </Pressable>
                        </View>
                      ) : null}

                      {/* Картинка – если есть assistantImages */}
                      {assistantImages && assistantImages.length > 0 && (
                        <Pressable
                          onPress={() => {
                            const lastImage =
                              assistantImages[assistantImages.length - 1];
                            setSelectedImage(lastImage.url);
                            setImageModalVisible(true);
                          }}
                          style={{
                            marginTop: currentAssistantReply ? 6 : 0,
                          }}
                        >
                          <Image
                            source={{
                              uri: assistantImages[assistantImages.length - 1]
                                .url,
                            }}
                            style={styles.imageBubbleImage}
                            resizeMode="contain"
                          />
                        </Pressable>
                      )}
                    </View>
                  </View>
                )}

                {/* 4) История сообщений — ВСЕГДА ниже статус/картинок */}
                {conversationHistory.map((msg, index) => (
                  <View key={index} style={styles.messageRow}>
                    <View
                      style={[
                        styles.messageBubbleBase,
                        msg.role === "user"
                          ? styles.messageBubbleUser
                          : styles.messageBubbleAssistant,
                      ]}
                    >
                      <View>
                        {/* текст сообщения */}
                        <Text style={styles.messageBubbleText}>
                          {msg.role === "user" ? "👤 " : "👶 "}
                          {renderFormattedText(msg.content).map((part, i) => {
                            if (part.type === "italic") {
                              return (
                                <Text key={i} style={{ fontStyle: "italic" }}>
                                  {part.content}
                                </Text>
                              );
                            }
                            return <Text key={i}>{part.content}</Text>;
                          })}
                        </Text>

                        {/* 🔊 кнопка только для ассистента */}
                        {msg.role === "assistant" && msg.content ? (
                          <Pressable
                            onPress={() =>
                              speakAssistantText(
                                msg.content,
                                `history-${index}`,
                              )
                            }
                            style={styles.speakerButton}
                          >
                            <Text style={styles.speakerButtonText}>
                              {speakingKey === `history-${index}` ? "🔊" : "🔈"}
                            </Text>
                          </Pressable>
                        ) : null}
                      </View>

                      {/* картинки этого сообщения, если есть */}
                      {msg.images &&
                        msg.images.length > 0 &&
                        msg.images.map((img, i) => (
                          <Pressable
                            key={i}
                            onPress={() => {
                              setSelectedImage(img.url);
                              setImageModalVisible(true);
                            }}
                            style={{ marginTop: 6 }}
                          >
                            <Image
                              source={{ uri: img.url }}
                              style={styles.imageBubbleImage}
                              resizeMode="contain"
                            />
                          </Pressable>
                        ))}
                    </View>
                  </View>
                ))}
              </ScrollView>

              <Pressable onPress={() => setShowChangelog(true)}>
                <Text style={styles.versionTag}>v{version}</Text>
              </Pressable>
            </View>
            <Modal
              visible={showChangelog}
              animationType="slide"
              transparent={true}
              onRequestClose={() => setShowChangelog(false)}
            >
              <View style={styles.modalOverlay}>
                <View style={styles.modalBox}>
                  <Text style={styles.modalTitle}>
                    {t(locale, "changelogTitle")}
                  </Text>
                  <ScrollView>
                    <Text style={styles.modalText}>{changelogText}</Text>
                  </ScrollView>
                  <Pressable onPress={() => setShowChangelog(false)}>
                    <Text style={styles.modalClose}>
                      {t(locale, "closeButton")}
                    </Text>
                  </Pressable>
                </View>
              </View>
            </Modal>
            {/* Модалка для changelog уже есть выше */}
            <Modal
              visible={imageModalVisible}
              transparent
              animationType="fade"
              onRequestClose={() => setImageModalVisible(false)}
            >
              <Pressable
                style={styles.fullImageOverlay}
                onPress={() => setImageModalVisible(false)}
              >
                <View style={styles.fullImageBox}>
                  {selectedImage && (
                    <Image
                      source={{ uri: selectedImage }}
                      style={styles.fullImage}
                      resizeMode="contain"
                    />
                  )}
                </View>
              </Pressable>
            </Modal>
          </View>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
    paddingTop: 50,
    paddingBottom: 16,
    backgroundColor: "#fff",
  },
  title: {
    fontSize: 28,
    fontFamily: FONT.bold,
    marginBottom: 10,
    textAlign: "center",
  },
  input: {
    flex: 1,
    height: 42,
    maxHeight: 120,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontFamily: FONT.regular,
    fontSize: 16,
  },
  replyContainer: {
    position: "relative",
    marginTop: 20,
    flex: 1, // 👈 контейнер чата растягивается
  },
  replyBox: {
    // marginTop: 20,
    backgroundColor: "#e0f2ff", // голубой
    padding: 10,
    borderRadius: 8,
    flex: 1, // 👈 вместо фиксированной высоты
  },
  replyImage: {
    marginTop: 10,
    marginBottom: 10,
    width: 220,
    height: 220,
    borderRadius: 8,
    alignSelf: "flex-start", // 👈 левый край
  },
  imageBubbleAssistant: {
    maxWidth: "80%",
    borderRadius: 18,
    padding: 4,
    backgroundColor: "rgba(204, 228, 255, 0.85)", // как у messageBubbleAssistant
    borderWidth: 1.5,
    borderColor: "rgba(120, 160, 200, 0.6)",
    alignSelf: "flex-start",

    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 1, height: 2 },
    elevation: 2,
  },

  imageBubbleImage: {
    width: 220,
    height: 220,
    borderRadius: 14,
  },
  systemLine: {
    fontSize: 18,
    fontFamily: FONT.bold,
    marginBottom: 8,
  },
  messageLine: {
    fontSize: 16,
    fontFamily: FONT.regular,
    marginBottom: 4,
  },
  // 👉 НОВОЕ: общая строка сообщения
  messageRow: {
    marginBottom: 6,
    flexDirection: "row",
  },

  // 👉 базовый стиль баббла
  messageBubbleBase: {
    maxWidth: "90%",
    borderRadius: 14,
    paddingVertical: 6,
    paddingHorizontal: 10,
  },

  // 👉 баббл ассистента (Мишка)
  messageBubbleAssistant: {
    backgroundColor: "rgba(204, 228, 255, 0.85)", // полупрозрачный голубой
    borderWidth: 1.5,
    borderColor: "rgba(120, 160, 200, 0.6)", // мягкая синяя обводка
    alignSelf: "flex-start",

    // мультяшная тень
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 1, height: 2 },
    elevation: 2, // Android
  },

  // 👉 баббл пользователя
  messageBubbleUser: {
    backgroundColor: "rgba(210, 245, 225, 0.85)", // нежно-зеленый полупрозрачный
    borderWidth: 1.5,
    borderColor: "rgba(150, 200, 170, 0.6)", // мягкая зелёная рамка
    alignSelf: "flex-start",

    // лёгкая тень (как у ассистента)
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 3,
    shadowOffset: { width: 1, height: 2 },
    elevation: 1,
  },

  // 👉 текст внутри баббла
  messageBubbleText: {
    fontSize: 18,
    // если используешь FONT:
    fontFamily: FONT.regular,
    color: "#102A43", // тёмный синий, хорошо читается
    lineHeight: 20,
  },
  messageBubbleTextBold: {
    fontSize: 18,
    // если используешь FONT:
    fontFamily: FONT.bold,
    fontWeight: "bold",
    color: "#102A43", // тёмный синий, хорошо читается
    lineHeight: 20,
  },
  versionTag: {
    position: "absolute",
    bottom: 4,
    right: 10,
    fontSize: 13,
    fontFamily: FONT.regular,
    color: "#888",
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  modalBox: {
    width: "80%",
    backgroundColor: "#fff",
    borderRadius: 10,
    padding: 20,
    maxHeight: "70%",
  },
  modalTitle: {
    fontSize: 18,
    fontFamily: FONT.bold,
    marginBottom: 10,
  },
  modalText: {
    fontSize: 16,
    fontFamily: FONT.regular,
    lineHeight: 20,
  },
  modalClose: {
    textAlign: "center",
    marginTop: 20,
    color: "#007aff",
  },
  fullImageOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.85)",
    justifyContent: "center",
    alignItems: "center",
  },
  fullImageBox: {
    width: "92%",
    height: "85%",
  },
  fullImage: {
    width: "100%",
    height: "100%",
    borderRadius: 12,
  },
  localeSwitcher: {
    position: "absolute",
    top: -38, // как у replyBox margin/padding сверху
    right: 0, // совпадает с правыми отступами UI
    zIndex: 200,
  },
  localeButton: {
    backgroundColor: "rgba(255,255,255,0.6)",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 12,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 4,
    shadowOffset: { width: 1, height: 2 },
    elevation: 2, // Android тень
  },
  localeFlag: {
    width: 24,
    height: 24,
    borderRadius: 4, // слегка скруглить, как иконки в мессенджерах
    resizeMode: "cover",
  },
  drawButton: {
    width: 42,
    height: 42,
    borderRadius: 5,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.75)",
    borderWidth: 1,
    borderColor: "#ccc",
  },

  drawButtonActive: {
    backgroundColor: "rgba(204, 228, 255, 0.95)",
    borderColor: "rgba(120, 160, 200, 0.8)",
  },

  drawButtonText: {
    fontSize: 20,
    lineHeight: 22,
  },

  inputRow: {
    flexDirection: "row",
    alignItems: "center", // ← ключевая строка
    width: "100%",
    gap: 6,
    marginBottom: 10,
    overflow: "visible",
    zIndex: 20,
  },

  drawControlWrapper: {
    position: "relative",
    zIndex: 30,
  },

  drawStyleMenuDown: {
    position: "absolute",
    top: 48,
    right: 0,
    gap: 6,
    zIndex: 40,
    alignItems: "center",
  },

  drawStyleButton: {
    width: 42,
    height: 42,
    borderRadius: 5,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.9)",
    borderWidth: 1,
    borderColor: "#ccc",
  },

  drawStyleButtonActive: {
    backgroundColor: "rgba(204, 228, 255, 0.95)",
    borderColor: "rgba(120, 160, 200, 0.9)",
  },

  drawStyleIcon: {
    fontSize: 20,
  },

  speakerButton: {
    alignSelf: "flex-end",
    // marginTop: 6,
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.55)",
  },

  speakerButtonText: {
    fontSize: 16,
  },

  voiceButton: {
    width: 42,
    height: 42,
    borderRadius: 5,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.75)",
    borderWidth: 1,
    borderColor: "#ccc",
  },

  voiceButtonText: {
    fontSize: 20,
  },

  disabledControl: {
    opacity: 0.35,
  },

  voiceOverlay: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    height: 42,
    borderRadius: 8,
    backgroundColor: "rgba(224, 242, 255, 0.96)",
    borderWidth: 1,
    borderColor: "rgba(120, 160, 200, 0.8)",
    zIndex: 100,
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: 14,
    paddingRight: 8,
  },

  voiceStopButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.9)",
    borderWidth: 1,
    borderColor: "rgba(120, 160, 200, 0.7)",
  },

  voiceStopButtonText: {
    fontSize: 18,
    color: "#102A43",
  },
  pageWrapper: {
    flex: 1,
    alignItems: "center",
    width: "100%",
  },

  contentWrapper: {
    flex: 1,
    width: "100%",
    maxWidth: 800,
    position: "relative",
  },

  appContent: {
    flex: 1,
    width: "100%",
  },

  voiceOverlayButtons: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },

  waveformRow: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },

  waveformContainer: {
    width: 90, // 👈 фиксируем ширину
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },

  waveBar: {
    width: 4,
    borderRadius: 2,
    backgroundColor: "#102A43",
    opacity: 0.8,
  },

  voiceStatusText: {
    marginLeft: 10,
    fontSize: 16,
    fontFamily: FONT.bold,
    color: "#5b5a5aff",
  },
});
